/**
 * In-app PDF viewer (ADR-0009 §3): PDF.js with the approved mockup's toolbar —
 * page thumbnails, zoom, find-in-document - and the controlled-copy watermark
 * and footer stamp drawn over every page (not burned into the file).
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as pdfjs from "pdfjs-dist";
import { EventBus, PDFFindController, PDFLinkService, PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import "pdfjs-dist/web/pdf_viewer.css";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Icon } from "./Icon";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface Stamp { watermark: string; left: string; right: string; draft: boolean }

export function PdfViewer({ url, placeholder, stamp, tools }: {
  url: string | null;
  /** Shown instead of the document (copy not ready, failed, no access). */
  placeholder?: ReactNode;
  stamp: Stamp;
  /** Extra toolbar items (version switch, downloads). */
  tools?: ReactNode;
}) {
  const container = useRef<HTMLDivElement>(null);
  const viewerEl = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<PDFViewer | null>(null);
  const busRef = useRef<EventBus | null>(null);
  const stampRef = useRef(stamp);
  stampRef.current = stamp;

  const [doc, setDoc] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [thumbs, setThumbs] = useState(true);
  const [find, setFind] = useState("");
  const [matches, setMatches] = useState<number | null>(null);

  // Set up the viewer once.
  useEffect(() => {
    if (!container.current || !viewerEl.current) return;
    const bus = new EventBus();
    const link = new PDFLinkService({ eventBus: bus });
    const findController = new PDFFindController({ eventBus: bus, linkService: link });
    const viewer = new PDFViewer({ container: container.current, viewer: viewerEl.current, eventBus: bus, linkService: link, findController, removePageBorders: false });
    link.setViewer(viewer);
    bus.on("pagesinit", () => { viewer.currentScaleValue = "page-width"; });
    bus.on("scalechanging", (e: { scale: number }) => setScale(e.scale));
    bus.on("pagechanging", (e: { pageNumber: number }) => setPage(e.pageNumber));
    bus.on("updatefindmatchescount", (e: { matchesCount: { total: number } }) => setMatches(e.matchesCount.total));
    bus.on("updatefindcontrolstate", (e: { matchesCount?: { total: number } }) => { if (e.matchesCount) setMatches(e.matchesCount.total); });
    bus.on("pagerendered", (e: { pageNumber: number; source: { div: HTMLDivElement } }) => decorate(e.source.div, e.pageNumber));
    viewerRef.current = viewer;
    busRef.current = bus;
    return () => { viewer.setDocument(null as unknown as pdfjs.PDFDocumentProxy); };
  }, []);

  function decorate(div: HTMLDivElement, n: number) {
    const s = stampRef.current;
    div.querySelector(".dv-overlay")?.remove();
    const o = document.createElement("div");
    o.className = `dv-overlay ${s.draft ? "draft" : ""}`;
    const wm = document.createElement("div");
    wm.className = "dv-wm";
    const span = document.createElement("span");
    span.textContent = s.watermark;
    wm.appendChild(span);
    const st = document.createElement("div");
    st.className = "dv-stamp";
    const l = document.createElement("span");
    l.textContent = s.left;
    const r = document.createElement("span");
    r.textContent = `${s.right} · ${n}/${viewerRef.current?.pagesCount ?? n}`;
    st.append(l, r);
    o.append(wm, st);
    div.appendChild(o);
  }

  // Load the document when the URL changes.
  useEffect(() => {
    let cancelled = false;
    setDoc(null); setError(null); setMatches(null); setPage(1);
    if (!url) { viewerRef.current?.setDocument(null as unknown as pdfjs.PDFDocumentProxy); return; }
    const task = pdfjs.getDocument({ url });
    task.promise.then(
      (d) => { if (cancelled) return; setDoc(d); viewerRef.current?.setDocument(d); (viewerRef.current as unknown as { linkService: PDFLinkService }).linkService.setDocument(d); },
      (e: Error) => { if (!cancelled) setError(e?.message?.includes("Missing PDF") ? "This file isn't a readable PDF." : "The document couldn't be loaded. Try again."); }
    );
    return () => { cancelled = true; void task.destroy(); };
  }, [url]);

  // Re-stamp rendered pages when the stamp text changes (e.g. switching revisions).
  useEffect(() => {
    container.current?.querySelectorAll<HTMLDivElement>(".page[data-page-number]").forEach((div) => {
      if (div.querySelector("canvas")) decorate(div, Number(div.dataset.pageNumber));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp.watermark, stamp.left, stamp.right, stamp.draft]);

  // Find in document.
  useEffect(() => {
    const bus = busRef.current;
    if (!bus || !doc) return;
    const t = setTimeout(() => {
      if (find.trim().length < 2) { setMatches(null); bus.dispatch("find", { source: null, type: "", query: "", highlightAll: false }); return; }
      bus.dispatch("find", { source: null, type: "", query: find.trim(), caseSensitive: false, entireWord: false, highlightAll: true, findPrevious: false, matchDiacritics: false });
    }, 200);
    return () => clearTimeout(t);
  }, [find, doc]);

  const zoom = (dz: number) => {
    const v = viewerRef.current;
    if (!v) return;
    v.currentScale = Math.min(3, Math.max(0.4, Math.round((v.currentScale + dz) * 10) / 10));
  };
  const goto = (n: number) => { if (viewerRef.current) viewerRef.current.currentPageNumber = n; };

  return (
    <section className="viewer" aria-label="Document viewer">
      <div className="vbar">
        <button className={`ib ${thumbs ? "on" : ""}`} onClick={() => setThumbs((t) => !t)} title="Thumbnails">▦ Pages</button>
        <span className="sep" />
        <button className="ib" onClick={() => zoom(-0.1)} title="Zoom out" aria-label="Zoom out">−</button>
        <span className="mono" style={{ minWidth: 44, textAlign: "center" }}>{Math.round(scale * 100)}%</span>
        <button className="ib" onClick={() => zoom(0.1)} title="Zoom in" aria-label="Zoom in">+</button>
        <span className="sep" />
        <label className="find">
          <Icon name="search" size={14} />
          <input placeholder="Find in document" value={find} onChange={(e) => setFind(e.target.value)} aria-label="Find in document"
            onKeyDown={(e) => { if (e.key === "Enter" && busRef.current) busRef.current.dispatch("find", { source: null, type: "again", query: find.trim(), caseSensitive: false, entireWord: false, highlightAll: true, findPrevious: e.shiftKey, matchDiacritics: false }); }} />
        </label>
        {matches !== null && find.trim().length >= 2 && <span>{matches} match{matches === 1 ? "" : "es"}</span>}
        {tools}
      </div>
      <div className="vbody">
        <Thumbs doc={doc} page={page} onPick={goto} hidden={!thumbs || !doc} />
        <div className="pdf-wrap">
          <div ref={container} className="pdf-scroll" style={{ visibility: url && doc && !placeholder ? "visible" : "hidden" }}>
            <div ref={viewerEl} className="pdfViewer" />
          </div>
          {(placeholder || error || (url && !doc)) && (
            <div className="viewer-msg">{placeholder ?? (error ? <div><b>Couldn't open the PDF.</b><div>{error}</div></div> : <div className="spin">Loading document…</div>)}</div>
          )}
        </div>
      </div>
    </section>
  );
}

function Thumbs({ doc, page, onPick, hidden }: { doc: pdfjs.PDFDocumentProxy | null; page: number; onPick: (n: number) => void; hidden: boolean }) {
  const [urls, setUrls] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let cancelled = false;
    setUrls([]);
    if (!doc) return;
    (async () => {
      const max = Math.min(doc.numPages, 60);
      for (let i = 1; i <= max; i++) {
        const pg = await doc.getPage(i);
        const vp = pg.getViewport({ scale: 1 });
        const s = 140 / vp.width;
        const v2 = pg.getViewport({ scale: s });
        const c = document.createElement("canvas");
        c.width = Math.ceil(v2.width); c.height = Math.ceil(v2.height);
        await pg.render({ canvas: c, canvasContext: c.getContext("2d")!, viewport: v2 }).promise;
        if (cancelled) return;
        const u = c.toDataURL("image/png");
        setUrls((prev) => [...prev, u]);
      }
    })().catch(() => { /* thumbnails are optional */ });
    return () => { cancelled = true; };
  }, [doc]);
  useEffect(() => { ref.current?.querySelector(".th-img.on")?.scrollIntoView({ block: "nearest" }); }, [page]);
  return (
    <div className={`thumbs ${hidden ? "hide" : ""}`} ref={ref}>
      {urls.map((u, i) => (
        <button key={i} className={`th-img ${page === i + 1 ? "on" : ""}`} onClick={() => onPick(i + 1)} aria-label={`Page ${i + 1}`}>
          <img src={u} alt="" />
          <span>{i + 1}</span>
        </button>
      ))}
    </div>
  );
}
